import React, { useMemo, useState } from 'react';
import { Bookmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SaveFiltersetModal } from './SaveFiltersetModal';
import { generateFilterExpression, sanitizeUrlFilterExpression } from '@/shared/utils/filterUtils';
import type { Filterset } from '@/app/services/filtersetService';
import Tooltip from '@/shared/components/Tooltip';

export interface SaveFilterButtonProps {
  onSave: (name: string, isShared: boolean) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentFilters: Record<string, any>;
  filterExpression?: string;
  disabled?: boolean;
  savedFilters?: Filterset[];
  targetModel?: string;
}

export const SaveFilterButton: React.FC<SaveFilterButtonProps> = ({
  onSave,
  currentFilters,
  filterExpression,
  disabled = false,
  savedFilters,
  targetModel,
}) => {
  const { t } = useTranslation();
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const normalizedFilterExpression = useMemo(() => {
    const rawExpression = filterExpression ?? generateFilterExpression(currentFilters);
    return sanitizeUrlFilterExpression(rawExpression);
  }, [currentFilters, filterExpression]);
  const isDisabled = disabled || normalizedFilterExpression.length === 0;

  return (
    <>
      <Tooltip text={t('shared.saveAs', 'Save as')} position="bottom" contentClassName="!whitespace-nowrap max-w-none">
        <button
          onClick={() => {
            if (isDisabled) {
              return;
            }

            setIsSaveModalOpen(true);
          }}
          className="w-8 h-8 p-2 border border-border rounded flex items-center justify-center hover:bg-muted"
          aria-label={t('shared.saveAs', 'Save as')}
          disabled={isDisabled}
        >
          <Bookmark className="w-4 h-4 text-text-muted" />
        </button>
      </Tooltip>
      <SaveFiltersetModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={onSave}
        currentFilters={currentFilters}
        filterExpression={normalizedFilterExpression}
        {...(savedFilters ? { savedFilters } : {})}
        {...(targetModel ? { targetModel } : {})}
      />
    </>
  );
};
